import { zodResolver } from '@hookform/resolvers/zod';
import { CircleDollarSign } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { usePricingImpact, useUpdatePlanPricing } from '../hooks/usePlanPricing';
import { usePlanById } from '../hooks/usePlans';
import { updatePlanPricingSchema, type UpdatePlanPricingFormValues } from '../schemas/plan-schema';
import type { UpdatePlanPricingRequest } from '../types/plans.types';
import { formatPlanPrice } from '../utils/plan-sync-status';
import { Notification } from '@/shared/components/ahkinia/Notification/notification';
import { Switch } from '@/shared/components/ahkinia/Switch/switch';
import { FormControl, FormField, FormItem } from '@/shared/components/ui/form';
import { Input } from '@/shared/components/ui/input';
import { DialogFooter as ModalFooter, ModalForm } from '@/shared/components/ui/modal-form';
import { UnifiedButton } from '@/shared/components/ui/unified-button';
import { formatIsoDate } from '@/shared/utils/format-date.utils';

interface EditPlanPricingModalProps {
  planId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** La API agenda la migración a 30 días; esto es solo la vista previa de esa fecha. */
const NOTICE_DAYS = 30;

const previewEffectiveDate = (): string => {
  const date = new Date();
  date.setDate(date.getDate() + NOTICE_DAYS);
  return formatIsoDate(date.toISOString());
};

export function EditPlanPricingModal({ planId, open, onOpenChange }: EditPlanPricingModalProps) {
  const { plan, isLoading } = usePlanById(open ? planId : '');
  const { impact } = usePricingImpact(open ? planId : '');
  const { updatePricing, isUpdating } = useUpdatePlanPricing(planId);

  const form = useForm<UpdatePlanPricingFormValues>({
    resolver: zodResolver(updatePlanPricingSchema),
    // Desactivado a propósito: un error de captura no debe cambiarle el cobro a nadie.
    defaultValues: { priceMonthly: 0, priceAnnual: 0, applyToExistingSubscribers: false },
  });

  const { reset } = form;
  const applyToExisting = form.watch('applyToExistingSubscribers');

  useEffect(() => {
    if (!plan) return;
    reset({
      priceMonthly: plan.priceMonthly,
      priceAnnual: plan.priceAnnual,
      applyToExistingSubscribers: false,
    });
  }, [plan, reset]);

  const handleClose = () => {
    if (!isUpdating) {
      onOpenChange(false);
    }
  };

  const onSubmit = (values: UpdatePlanPricingFormValues) => {
    const payload: UpdatePlanPricingRequest = values;

    updatePricing(payload, {
      onSuccess: () => {
        Notification.success({
          title: values.applyToExistingSubscribers
            ? 'Precio actualizado y migración agendada'
            : 'Precio actualizado',
        });
        onOpenChange(false);
      },
    });
  };

  const isBusy = isLoading || isUpdating;
  const affected = impact?.activeSubscriptions ?? 0;

  return (
    <ModalForm
      open={open}
      headerIcon={<CircleDollarSign />}
      onOpenChange={handleClose}
      title="Cambiar precio"
      description="Stripe no permite modificar un precio, así que se crea uno nuevo y se archiva el anterior."
      className="sm:max-w-[560px]"
      data-testid="edit-plan-pricing-modal"
      form={form}
      onSubmit={onSubmit}
      footer={
        <ModalFooter>
          <div className="flex flex-row justify-end w-full gap-4">
            <UnifiedButton
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isBusy}
              data-testid="cancel-pricing-btn"
            >
              Cancelar
            </UnifiedButton>
            <UnifiedButton
              type="submit"
              variant="outline"
              actionType="submit"
              disabled={isBusy}
              data-testid="save-pricing-btn"
            >
              Guardar
            </UnifiedButton>
          </div>
        </ModalFooter>
      }
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormField
          control={form.control}
          name="priceMonthly"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <Input
                  label="Precio mensual (MXN)"
                  type="number"
                  min={0}
                  {...field}
                  onChange={(e) => field.onChange(Number(e.target.value) || 0)}
                  disabled={isBusy}
                  error={form.formState.errors.priceMonthly?.message}
                  data-testid="pricing-monthly-input"
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="priceAnnual"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <Input
                  label="Precio anual (MXN)"
                  type="number"
                  min={0}
                  {...field}
                  onChange={(e) => field.onChange(Number(e.target.value) || 0)}
                  disabled={isBusy}
                  error={form.formState.errors.priceAnnual?.message}
                  data-testid="pricing-annual-input"
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      {plan && (
        <p data-testid="pricing-current" className="text-primary text-xs">
          Precio actual: {formatPlanPrice(plan.priceMonthly)} mensual ·{' '}
          {formatPlanPrice(plan.priceAnnual)} anual
        </p>
      )}

      <div className="flex flex-col gap-3 rounded-xl border border-outline p-4">
        <FormField
          control={form.control}
          name="applyToExistingSubscribers"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Switch
                  label="Aplicar también a las cuentas ya suscritas"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={isBusy}
                  data-testid="apply-to-existing-switch"
                />
              </FormControl>
            </FormItem>
          )}
        />

        {/* El impacto es informativo y cambia de significado según la casilla: sin ella
            nadie cambia de cobro; con ella, todos esos clientes lo hacen en 30 días. */}
        {applyToExisting ? (
          <p data-testid="pricing-impact-migrate" className="text-warn text-xs">
            {affected === 0
              ? 'Ninguna cuenta está suscrita a este plan, así que no hay a quién migrar.'
              : `${affected} ${affected === 1 ? 'cuenta pasará' : 'cuentas pasarán'} al precio nuevo el ${previewEffectiveDate()}.`}
          </p>
        ) : (
          <p data-testid="pricing-impact-keep" className="text-primary text-xs">
            {affected === 0
              ? 'Ninguna cuenta está suscrita a este plan.'
              : `${affected} ${affected === 1 ? 'cuenta conserva' : 'cuentas conservan'} su precio actual. El cambio solo afecta a las contrataciones nuevas.`}
          </p>
        )}

        {applyToExisting && (
          <p data-testid="pricing-migration-notice" className="text-primary text-xs">
            Guardar no cobra nada hoy: agenda el cambio a {NOTICE_DAYS} días y le avisa por correo a
            cada cuenta. Hasta esa fecha se les sigue cobrando lo de siempre, y pueden cancelar
            antes si no aceptan el precio nuevo.
          </p>
        )}
      </div>
    </ModalForm>
  );
}
